/* PulsePage — dedicated activity feed at /pulse.
 *
 * Filter chips + infinite scroll (cursor-paginated) + auto-refresh top
 * of feed every 45s. Cards show profile image, name (+ verified check),
 * event icon, headline, timestamp, and a CTA sized for tap.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity, ArrowRight, ShieldCheck, BadgeCheck, Sparkles, Newspaper, Film, Video,
  Pencil, Link as LinkIcon, Flag, Trophy, Award, Handshake, Radio, Rocket,
  Megaphone, Flame, UserPlus, ListChecks, ArrowRightLeft, Users,
} from 'lucide-react';
import VerifiedCheck from '../components/VerifiedCheck';
import { timeAgo } from '../components/pulse/ThePulseStrip';
import PulseEventDetailModal from '../components/pulse/PulseEventDetailModal';
import PulseReactions from '../components/pulse/PulseReactions';
import PulseSpotlightRails from '../components/pulse/PulseSpotlightRails';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: 'all',                label: 'All' },
  { key: 'following',          label: 'Following' },
  { key: 'athletes',           label: 'Athletes' },
  { key: 'programs',           label: 'Programs' },
  { key: 'brands',             label: 'Brands' },
  { key: 'stories',            label: 'Stories' },
  { key: 'transfers',          label: 'Transfers' },
  { key: 'nil',                label: 'NIL' },
  { key: 'nfl',                label: 'NFL' },
  { key: 'nba',                label: 'NBA' },
  { key: 'mlb',                label: 'MLB' },
  { key: 'college-football',   label: 'College Football' },
  { key: 'college-basketball', label: 'College Basketball' },
];

const ICON_MAP = {
  'shield-check': ShieldCheck, 'badge-check': BadgeCheck, 'sparkles': Sparkles,
  'newspaper': Newspaper, 'film': Film, 'video': Video, 'pencil': Pencil,
  'link': LinkIcon, 'flag': Flag, 'arrow-right-left': ArrowRightLeft,
  'trophy': Trophy, 'award': Award, 'handshake': Handshake, 'radio': Radio,
  'users-plus': UserPlus, 'user-plus': UserPlus, 'list': ListChecks,
  'rocket': Rocket, 'megaphone': Megaphone, 'flame': Flame,
  'activity': Activity, 'users': Users,
};

const TONE = {
  blue: { bg: 'bg-blue-500/10', ring: 'ring-blue-400/30', text: 'text-blue-300' },
  emerald: { bg: 'bg-emerald-500/10', ring: 'ring-emerald-400/30', text: 'text-emerald-300' },
  green: { bg: 'bg-green-500/10', ring: 'ring-green-400/30', text: 'text-green-300' },
  amber: { bg: 'bg-amber-500/10', ring: 'ring-amber-400/30', text: 'text-amber-300' },
  slate: { bg: 'bg-white/[0.05]', ring: 'ring-white/15', text: 'text-white/70' },
  purple: { bg: 'bg-purple-500/10', ring: 'ring-purple-400/30', text: 'text-purple-300' },
  orange: { bg: 'bg-orange-500/10', ring: 'ring-orange-400/30', text: 'text-orange-300' },
  cyan: { bg: 'bg-cyan-500/10', ring: 'ring-cyan-400/30', text: 'text-cyan-300' },
  rose: { bg: 'bg-rose-500/10', ring: 'ring-rose-400/30', text: 'text-rose-300' },
};

const ActivityCard = ({ ev, onOpen, onEventUpdate }) => {
  const Icon = ICON_MAP[ev.icon] || Activity;
  const t = TONE[ev.tone] || TONE.slate;
  return (
    <article
      className={`rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-4 sm:p-5 hover:ring-white/25 transition cursor-pointer`}
      data-testid={`pulse-event-${ev.id}`}
      onClick={() => onOpen(ev)}
    >
      <div className="flex items-start gap-3">
        {/* Avatar / thumbnail */}
        {ev.image_url ? (
          <img
            src={ev.image_url}
            alt=""
            loading="lazy"
            className="w-12 h-12 rounded-full ring-2 ring-white/10 object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full ring-2 ring-white/10 bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center flex-shrink-0">
            <Icon className={`w-5 h-5 ${t.text}`} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ring-1 ${t.bg} ${t.ring} ${t.text}`}>
              <Icon className="w-3 h-3" />
              {(ev.event_type || '').replace(/_/g, ' ')}
            </span>
            <span className="text-[11px] text-white/40 font-mono">· {timeAgo(ev.created_at)}</span>
          </div>
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <p className="text-sm sm:text-base font-bold text-white truncate">
              {ev.actor_name}
            </p>
            {ev.verified && <VerifiedCheck size={14} />}
          </div>
          <p className="text-sm text-white/80 leading-snug break-words">{ev.headline}</p>
          {(ev.metadata?.sport || ev.metadata?.team || ev.metadata?.category) && (
            <p className="text-[11px] uppercase tracking-widest text-white/40 mt-1 truncate">
              {[ev.metadata.sport, ev.metadata.team, ev.metadata.category].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        {/* CTA — opens the detail modal in-place */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen(ev); }}
          className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-black text-[10px] font-black uppercase tracking-widest hover:bg-blue-200 transition"
          data-testid={`pulse-event-cta-${ev.id}`}
        >
          {ev.cta_label} <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {/* iter234 · Reactions row — hearts + claps live under every card. */}
      <div className="mt-3 flex items-center justify-between gap-2 pt-2 border-t border-white/[0.06]">
        <PulseReactions ev={ev} size="sm" onUpdate={onEventUpdate} />
      </div>
    </article>
  );
};

export default function PulsePage() {
  const [filter, setFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // iter233 · Tapping a card (or the View pill) opens a detail modal
  // with the full context — hero image / embedded video, description,
  // and any custom links the founder attached. Portaled to <body>.
  const [openEvent, setOpenEvent] = useState(null);

  const fetchPage = useCallback(async (filt, cur) => {
    const params = new URLSearchParams({ filter: filt, limit: '20' });
    if (cur) params.set('cursor', cur);
    const { data } = await axios.get(`${API}/pulse/feed?${params.toString()}`, { withCredentials: true });
    return data;
  }, []);

  // Reload from scratch on filter change
  useEffect(() => {
    let cancel = false;
    const run = async () => {
      setLoading(true);
      try {
        const data = await fetchPage(filter, null);
        if (cancel) return;
        setEvents(data.events || []);
        setCursor(data.next_cursor || null);
      } catch {
        if (!cancel) { setEvents([]); setCursor(null); }
      } finally { if (!cancel) setLoading(false); }
    };
    run();
    return () => { cancel = true; };
  }, [filter, fetchPage]);

  // Auto-refresh top-of-feed every 45s (only when filter is All/Following)
  useEffect(() => {
    if (filter !== 'all' && filter !== 'following') return undefined;
    const t = setInterval(async () => {
      try {
        const data = await fetchPage(filter, null);
        const nextIds = new Set((data.events || []).map(e => e.id));
        // Only refresh if the top event id changed
        if (!events[0] || events[0].id !== (data.events || [])[0]?.id) {
          // Merge: keep unseen bottom events, prepend new
          const preserved = events.filter(e => !nextIds.has(e.id));
          setEvents([...(data.events || []), ...preserved].slice(0, 60));
        }
      } catch { /* silent */ }
    }, 45_000);
    return () => clearInterval(t);
  }, [filter, events, fetchPage]);

  // iter234 · Merge server-side reaction updates back into the list
  // so re-renders (or the modal closing) keep counts in sync.
  const patchEvent = useCallback((updated) => {
    if (!updated?.id) return;
    setEvents((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));
    setOpenEvent((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  }, []);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await fetchPage(filter, cursor);
      setEvents((prev) => [...prev, ...(data.events || [])]);
      setCursor(data.next_cursor || null);
    } finally { setLoadingMore(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="pulse-page">
      {/* Header */}
      <div className="border-b border-white/10 bg-gradient-to-b from-black to-neutral-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <div className="flex items-center gap-2 mb-3">
            <span className="relative inline-flex w-2.5 h-2.5">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
              <span className="relative inline-block w-2.5 h-2.5 rounded-full bg-emerald-400" />
            </span>
            <p className="text-[10px] uppercase tracking-[0.3em] font-black text-emerald-300">Live</p>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-[1.02] text-white">
            The Pulse
          </h1>
          <p className="mt-2 text-sm text-white/60 max-w-xl">
            Every claim, every story, every signing — as it happens on Flythouse.
          </p>
        </div>
      </div>

      {/* iter237 · Athlete spotlight — Top Athletes (auto-ranked) +
          Athletes to Look Out For (admin-curated), sport-filterable. */}
      <PulseSpotlightRails />

      {/* Filter chips */}
      <div className="sticky top-0 z-20 border-b border-white/10 bg-neutral-950/90 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 overflow-x-auto no-scrollbar">
          <div className="flex gap-2" data-testid="pulse-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold transition ring-1 ${
                  filter === f.key
                    ? 'bg-white text-black ring-white'
                    : 'bg-white/5 text-white/70 ring-white/10 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`pulse-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Feed */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-3">
        {loading && events.length === 0 && (
          <p className="text-center text-sm text-white/50 py-16" data-testid="pulse-loading">Loading feed…</p>
        )}
        {!loading && events.length === 0 && (
          <div className="rounded-3xl ring-1 ring-white/10 bg-white/[0.03] p-10 text-center" data-testid="pulse-empty">
            <Activity className="w-10 h-10 text-white/30 mx-auto mb-3" />
            <p className="text-white/70">Nothing here yet.</p>
            <p className="text-white/40 text-sm mt-1">
              {filter === 'following'
                ? 'Follow athletes, programs, and brands to build your feed.'
                : 'Check back in a bit — activity fills in as it happens.'}
            </p>
          </div>
        )}
        {events.map((ev) => (
          <ActivityCard
            key={ev.id}
            ev={ev}
            onOpen={setOpenEvent}
            onEventUpdate={patchEvent}
          />
        ))}

        {cursor && (
          <div className="pt-4 pb-8 text-center">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 hover:bg-white/15 text-white text-[11px] font-bold uppercase tracking-widest ring-1 ring-white/10 transition disabled:opacity-50"
              data-testid="pulse-load-more"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </main>

      {openEvent && (
        <PulseEventDetailModal
          ev={openEvent}
          onClose={() => setOpenEvent(null)}
          onEventUpdate={patchEvent}
        />
      )}
    </div>
  );
}
